import { POWERUP_LIST } from "@/shared/data/powerups";
import { usePhaserBounds } from "@/ui/hooks/usePhaserBounds";
import { useGameRuntime } from "../context/GameContext";
import { useGameStore } from "@/shared/state/gameStore";
import { computeDerivedStats } from "@/shared/utils/powerups";
import {
  AnimationPowerButtons,
  LikeButton,
  PowerupShopSheet,
  StreamChatFeed,
} from "../components";
import { useEffect, useMemo, useState } from "react";
import { Eye, Heart, Radio, Trophy } from "lucide-react";
import { useStreamChatFeed } from "../hooks/useStreamChatFeed";

interface HUDProps {
  rootRef: React.RefObject<HTMLDivElement | null>;
  phaserMountRef: React.RefObject<HTMLDivElement | null>;
}

const HUD = ({ rootRef, phaserMountRef }: HUDProps) => {
  const bounds = usePhaserBounds(rootRef, phaserMountRef);
  const [shouldRender, setShouldRender] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [showHelper, setShowHelper] = useState(true);

  const runtime = useGameRuntime();

  useEffect(() => {
    if (bounds.width > 0 && bounds.height > 0) {
      setShouldRender(true);
    }
  }, [bounds.width, bounds.height]);

  const viewers = useGameStore((state) => state.viewers);
  const hearts = useGameStore((state) => state.likes);
  const streamTitle = useGameStore((state) => state.streamTitle);
  const patchGame = useGameStore((state) => state.patchGame);
  const gameState = useGameStore();
  const runStatus = useGameStore((state) => state.run.status);
  const { messages, exitingIds, moderateMessage } = useStreamChatFeed(runtime);

  const audienceStats = useMemo(
    () => computeDerivedStats(gameState, POWERUP_LIST),
    [gameState],
  );

  const totalViewers = Math.floor(viewers.real + viewers.botted);

  useEffect(() => {
    if (runStatus === "won") {
      setShopOpen(false);
    }
  }, [runStatus]);

  if (!shouldRender) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
      }}
      className="pointer-events-none absolute"
    >
      <div className="pointer-events-auto h-full w-full overflow-hidden rounded-[2.4rem] border border-white/10 bg-[linear-gradient(180deg,rgba(6,7,14,0.08),rgba(6,7,14,0.24),rgba(4,5,12,0.68))] shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(192,132,252,0.18),_transparent_28%),linear-gradient(180deg,rgba(91,33,182,0.12),transparent_22%,transparent_68%,rgba(0,0,0,0.46))]" />

        <div className="relative flex h-full flex-col p-3 sm:p-4">
          <AnimationPowerButtons />

          <div className="rounded-[1.7rem] border border-fuchsia-200/10 bg-black/30 px-4 py-3 text-white backdrop-blur-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full bg-red-400 shadow-[0_0_12px_rgba(248,113,113,0.8)]" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.35em] text-fuchsia-100/72">
                    Streaming
                  </span>
                </div>

                {streamTitle.unlocked ? (
                  <input
                    value={streamTitle.value}
                    onChange={(event) =>
                      patchGame({
                        streamTitle: {
                          ...streamTitle,
                          value: event.target.value,
                        },
                      })
                    }
                    maxLength={48}
                    className="mt-2 w-full bg-transparent text-base font-semibold text-white outline-none placeholder:text-white/30 sm:text-lg"
                    placeholder="Name your stream"
                  />
                ) : (
                  <p className="mt-2 truncate text-base font-semibold text-white/92 sm:text-lg">
                    Untitled stream
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <div className="group relative rounded-full border border-fuchsia-200/10 bg-black/26 px-3 py-2 text-white backdrop-blur-md">
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <Eye className="h-3.5 w-3.5 text-fuchsia-200" />
                    <span>{totalViewers.toLocaleString()}</span>
                  </div>
                  <div className="pointer-events-none absolute top-[calc(100%+0.5rem)] right-0 w-40 rounded-2xl border border-fuchsia-200/10 bg-black/72 px-3 py-2 text-[11px] text-white/80 opacity-0 shadow-[0_16px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl transition group-hover:opacity-100">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-white/55">Real</span>
                      <span>{Math.floor(viewers.real).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <span className="text-white/55">Botted</span>
                      <span>{Math.floor(viewers.botted).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <span className="text-white/55">Flow</span>
                      <span>{`+${audienceStats.realViewerFlowPerSecond.toFixed(2)}/sec`}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <span className="text-white/55">Retention</span>
                      <span>{`${Math.round(audienceStats.realViewerRetention * 100)}%`}</span>
                    </div>
                  </div>
                </div>

                <div className="group relative rounded-full border border-fuchsia-200/10 bg-black/26 px-3 py-2 text-white backdrop-blur-md">
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <Heart className="h-3.5 w-3.5 text-red-300" />
                    <span>{Math.floor(hearts).toLocaleString()}</span>
                  </div>
                  <div className="pointer-events-none absolute top-[calc(100%+0.5rem)] right-0 w-40 rounded-2xl border border-fuchsia-200/10 bg-black/72 px-3 py-2 text-[11px] text-white/80 opacity-0 shadow-[0_16px_40px_rgba(0,0,0,0.35)] backdrop-blur-xl transition group-hover:opacity-100">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-white/55">Passive</span>
                      <span>{audienceStats.likesPerSecond.toFixed(1)}/sec</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showHelper ? (
            <div className="mt-4 max-w-[70%] rounded-[1.6rem] border border-fuchsia-200/10 bg-black/28 px-4 py-3 text-white backdrop-blur-xl transition">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-fuchsia-100/72">
                <Radio className="h-3 w-3" />
                Stream Feed
              </div>
              <p className="mt-2 text-sm font-semibold">
                Your audience is watching live.
              </p>
              <p className="mt-1 text-xs leading-relaxed text-white/65">
                Tap hearts, grow the crowd, and open the shop when you are
                ready.
              </p>
            </div>
          ) : null}

          <StreamChatFeed
            messages={messages}
            exitingIds={exitingIds}
            onModerate={moderateMessage}
          />

          <div className="flex-1" />

          <div className="relative z-10 flex items-center gap-2 pb-4 sm:gap-3 sm:pb-5 select-none">
            <button
              type="button"
              onClick={() => setShopOpen(true)}
              className="rounded-full border border-fuchsia-300/24 bg-black/28 px-4 py-3 text-sm font-semibold text-fuchsia-100 backdrop-blur-xl transition hover:bg-fuchsia-400/14"
            >
              Shop
            </button>
            <div className="flex-1" />
            <div className="rounded-full border border-fuchsia-200/10 bg-black/30 p-3 backdrop-blur-xl">
              <LikeButton
                onFirstLike={() => {
                  if (showHelper) {
                    setShowHelper(false);
                  }
                }}
                onClick={() => runtime.clickLike()}
              />
            </div>
          </div>

          <PowerupShopSheet
            open={shopOpen}
            onClose={() => setShopOpen(false)}
          />

          {runStatus === "won" ? (
            <div className="pointer-events-auto absolute inset-0 z-[220] flex items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.16),_transparent_32%),rgba(5,6,14,0.74)] p-5 backdrop-blur-md">
              <div className="w-full max-w-sm rounded-[2rem] border border-fuchsia-200/12 bg-black/55 p-6 text-white shadow-[0_30px_120px_rgba(0,0,0,0.52)] backdrop-blur-2xl">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-fuchsia-300/20 bg-fuchsia-400/12">
                    <Trophy className="h-6 w-6 text-fuchsia-100" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.34em] text-fuchsia-200/72">
                      Stream Complete
                    </p>
                    <h2 className="mt-1 text-xl font-semibold text-white">
                      You Won
                    </h2>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-white/72">
                  {streamTitle.value.trim().length > 0
                    ? `You wrapped "${streamTitle.value}" and retired the stream on top.`
                    : "You wrapped the stream and retired on top."}
                </p>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-[1.4rem] border border-white/10 bg-white/[0.04] px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-white/45">
                      Likes
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {Math.floor(hearts).toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-[1.4rem] border border-white/10 bg-white/[0.04] px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-white/45">
                      Viewers
                    </p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {totalViewers.toLocaleString()}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    runtime.resetRun();
                    setShowHelper(true);
                    setShopOpen(false);
                  }}
                  className="mt-5 w-full rounded-full bg-fuchsia-300/90 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-fuchsia-200"
                >
                  Start Next Stream
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default HUD;
